import { createSelector } from "reselect"
import { get, chain, pick, includes, pickBy, keyBy } from "lodash"
import { BETA_FACTORS } from "helpers/analyzeConstants"
import { getPortfolioBetaInstrument, getIsInvalidBetaInstrumentReason } from "modules/portfolio-root/selectors/beta-instrument"
import { buildContributorKey, formatBetaInstrumentTooltip } from "helpers/contributors"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { getSelectedModelId } from "modules/portfolio-root/selectors/portfolios"
import { getIsIntraday } from "./intraday"

const EMPTY_ARRAY = []
const BETA_TYPES = ["historical", "predicted", "dynamicPredicted"]

const getRawBetaSummary = state => get(state, "global.data.beta.data.model.simulation.beta.0", EMPTY_ARRAY)
const getDynamicPredictedBetaSummary = state => get(state, "global.data.dynamicBeta.data.model.simulation.dynamicBeta.0", EMPTY_ARRAY)
const getRebalancedRawBetaSummary = state => get(state, "modules.rebalance.rebalancedBeta", EMPTY_ARRAY)
const getRebalancedDynamicPredictedBetaSummary = state => get(state, "modules.rebalance.rebalancedDynamicPredictedBeta", EMPTY_ARRAY)

// Contributors
const getRawBetaContributors = state => get(state, "global.data.betaContributors.data.model.simulation.betaContributors.0.contributors", EMPTY_ARRAY)
const getDynamicPredictedBetaContributors = state => get(state, "global.data.dynamicBetaContributors.data.model.simulation.dynamicBetaContributors.0.contributors", EMPTY_ARRAY)
const getRawRebalancedBetaContributors = state => get(state, "modules.rebalance.rebalancedBetaContributors", EMPTY_ARRAY)
const getRebalancedDynamicBetaContributors = state => get(state, "modules.rebalance.rebalancedDynamicBetaContributors", EMPTY_ARRAY)

// Grouped Contributors
const getRawGroupedBetaContributors = state => get(state, "global.data.groupedBetaContributors.data.model.simulation.groupedBetaContributors.0.groupedContributors", EMPTY_ARRAY)
const getGroupedDynamicPredictedBetaContributors = state => get(state, "global.data.groupedDynamicBetaContributors.data.model.simulation.groupedDynamicBetaContributors.0.groupedContributors", EMPTY_ARRAY)
const getRawGroupedRebalancedBetaContributors = state => get(state, "modules.rebalance.rebalancedGroupedBetaContributors", EMPTY_ARRAY)
const getGroupedRebalancedDynamicBetaContributors = state => get(state, "modules.rebalance.rebalancedGroupedDynamicBetaContributors", EMPTY_ARRAY)

const getRawBetaFactors = createSelector(
  [getPortfolioBetaInstrument, getIsInvalidBetaInstrumentReason, getIsIntraday],
  (betaInstrument, invalidReason, isIntraday) => (betaInstrument || invalidReason) && !isIntraday ? {
    ...BETA_FACTORS,
    dynamicPredicted: {
      category: "beta",
      label: `Custom - ${get(formatBetaInstrumentTooltip(betaInstrument), "dynamicBetaId")}`,
      adjustedReason: invalidReason,
    },
  } : BETA_FACTORS,
)

export const getBetaFactors = createSelector(
  [getRawBetaFactors, getMissingDataFiles, getSelectedModelId],
  (betaFactors, missingDataFiles, modelId) => {
    const missing = get(missingDataFiles, modelId, [])
    return pickBy(betaFactors, (_, key) => !includes(missing, `${key}Beta`))
  },
)

export const getBetaSummary = createSelector(
  [getRawBetaSummary, getDynamicPredictedBetaSummary],
  (betaSummary, dynamicBetaSummary) => ({
    ...betaSummary,
    ...dynamicBetaSummary,
  }),
)

export const getRebalancedBetaSummary = createSelector(
  [getRebalancedRawBetaSummary, getRebalancedDynamicPredictedBetaSummary],
  (betaSummary, dynamicBetaSummary) => ({
    ...betaSummary,
    ...dynamicBetaSummary,
  }),
)

export const getBetaContributors = createSelector(
  [getRawBetaContributors, getDynamicPredictedBetaContributors],
  computeBetaContributors,
)

export const getGroupedBetaContributors = createSelector(
  [getRawGroupedBetaContributors, getGroupedDynamicPredictedBetaContributors],
  computeBetaContributors,
)

export const getRebalancedBetaContributors = createSelector(
  [getRawRebalancedBetaContributors, getRebalancedDynamicBetaContributors],
  computeBetaContributors,
)

export const getGroupedRebalancedBetaContributors = createSelector(
  [getRawGroupedRebalancedBetaContributors, getGroupedRebalancedDynamicBetaContributors],
  computeBetaContributors,
)

export function computeBetaContributors(betaContributors, dynamicBetaContributors) {
  const keyedBetaContributors = keyBy(betaContributors, buildContributorKey)
  const keyedDynamicBetaContributors = keyBy(dynamicBetaContributors, buildContributorKey)

  // merge() mutates its first arg, and keyedBetaContributors' values are live Redux state
  // objects; seed the chain with a fresh {} so both sources are deep-merged into a new object
  // instead of mutating the store (RTK immutableCheck).
  return chain({})
    .merge(keyedBetaContributors, keyedDynamicBetaContributors)
    .mapValues((contributor) => ({
      ...pick(contributor, BETA_TYPES),
      ...(get(contributor, "securityBeta") && { standalone: get(contributor, "securityBeta") }),
      ...(contributor.contributors && {
        contributors: computeBetaContributors(contributor.contributors),
      }),
    }))
    .value()
}
